import Dexie, { type DexieOptions, type Table } from "dexie";

import {
  DEFAULT_VIEWPORT,
  KIMNOTE_SCHEMA_VERSION,
  createEntityId,
  nowIso,
  type AssetRecord,
  type AssetRefRecord,
  type BoardRecord,
  type BoardSnapshot,
  type EntityId,
  type ProjectRecord,
  type SettingRecord,
  type StoredEdge,
  type StoredNode,
  type Viewport,
} from "../core/model";

export const DEFAULT_DATABASE_NAME = "kimnote";
export const KIMNOTE_DATABASE_VERSION = 2;

export class KimnoteDatabase extends Dexie {
  projects!: Table<ProjectRecord, EntityId>;
  boards!: Table<BoardRecord, EntityId>;
  nodes!: Table<StoredNode, EntityId>;
  edges!: Table<StoredEdge, EntityId>;
  assets!: Table<AssetRecord, string>;
  assetRefs!: Table<AssetRefRecord, [EntityId, string]>;
  settings!: Table<SettingRecord, string>;

  constructor(name = DEFAULT_DATABASE_NAME, options?: DexieOptions) {
    super(name, options);

    this.version(1).stores({
      projects: "&id, updatedAt, lastOpenedAt",
      boards: "&id, projectId, parentId, [projectId+updatedAt]",
      nodes: "&id, boardId, type, [boardId+zIndex], [boardId+updatedAt]",
      edges:
        "&id, boardId, source, target, [boardId+source], [boardId+target], [boardId+updatedAt]",
      assets: "&hash, createdAt",
      assetRefs: "[projectId+hash], projectId, hash",
      settings: "&key",
    });

    // Version 2 deliberately removes indexes that were never queried. IndexedDB
    // indexes duplicate keys in their own B-trees, so keeping only access-path
    // indexes materially reduces write amplification and on-disk size.
    this.version(KIMNOTE_DATABASE_VERSION).stores({
      projects: "&id, lastOpenedAt",
      boards: "&id, projectId, parentId",
      nodes: "&id, boardId, [boardId+zIndex]",
      edges: "&id, boardId",
      assets: "&hash",
      assetRefs: "[projectId+hash], projectId, hash",
      settings: "&key",
    });
  }
}

/** Browser singleton. Tests and import jobs may inject an isolated database instead. */
export const kimnoteDb = new KimnoteDatabase();

export interface CreateProjectOptions {
  id?: EntityId;
  boardId?: EntityId;
  title?: string;
  boardTitle?: string;
  now?: string;
}

export interface CreatedProject {
  project: ProjectRecord;
  board: BoardRecord;
}

export interface CreateBoardOptions {
  projectId: EntityId;
  parentId?: EntityId | null;
  title?: string;
  activate?: boolean;
}

export interface DuplicateBoardTreeOptions {
  parentId?: EntityId | null;
  title?: string;
}

export interface DuplicateBoardTreeResult {
  root: BoardRecord;
  boards: BoardRecord[];
  boardIdMap: ReadonlyMap<EntityId, EntityId>;
}

export interface DeleteBoardResult {
  deletedBoardId: EntityId;
  nextBoardId: EntityId;
  deletedPortalNodeIds: EntityId[];
}

export async function createDefaultProject(
  options: CreateProjectOptions = {},
  database: KimnoteDatabase = kimnoteDb,
): Promise<CreatedProject> {
  const { project, board } = makeDefaultProjectRecords(options);

  await database.transaction("rw", database.projects, database.boards, async () => {
    if ((await database.projects.get(project.id)) || (await database.boards.get(board.id))) {
      throw new Error("A project or board with the generated id already exists.");
    }
    await database.projects.add(project);
    await database.boards.add(board);
  });

  return { project, board };
}

/** Creates an independently addressable board without forcing navigation to it. */
export async function createBoardRecord(
  options: CreateBoardOptions,
  database: KimnoteDatabase = kimnoteDb,
): Promise<BoardRecord> {
  const timestamp = nowIso();
  return database.transaction("rw", database.projects, database.boards, async () => {
    const project = await database.projects.get(options.projectId);
    if (!project) throw new Error(`Project not found: ${options.projectId}`);
    if (options.parentId) {
      const parent = await database.boards.get(options.parentId);
      if (!parent || parent.projectId !== project.id) {
        throw new Error("Parent board must belong to the same project.");
      }
    }
    const board: BoardRecord = {
      id: createEntityId("board"),
      projectId: project.id,
      parentId: options.parentId ?? null,
      title: options.title?.trim() || "新画板",
      order: await database.boards.where("projectId").equals(project.id).count(),
      viewport: { ...DEFAULT_VIEWPORT },
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    await database.boards.add(board);
    await database.projects.update(project.id, {
      activeBoardId: options.activate ? board.id : project.activeBoardId,
      updatedAt: timestamp,
    });
    return board;
  });
}

/** Reparents a board while rejecting cross-project links and hierarchy cycles. */
export async function setBoardParent(
  boardId: EntityId,
  parentId: EntityId | null,
  database: KimnoteDatabase = kimnoteDb,
): Promise<BoardRecord> {
  return database.transaction("rw", database.projects, database.boards, async () => {
    const board = await database.boards.get(boardId);
    if (!board) throw new Error(`Board not found: ${boardId}`);
    if (parentId === board.id) throw new Error("画板不能成为自己的父级。");

    if (parentId) {
      const parent = await database.boards.get(parentId);
      if (!parent || parent.projectId !== board.projectId) {
        throw new Error("父级画板必须属于同一个项目。");
      }
      const boards = await database.boards.where("projectId").equals(board.projectId).toArray();
      const parentById = new Map(boards.map((item) => [item.id, item.parentId]));
      for (let cursor: EntityId | null = parentId; cursor; cursor = parentById.get(cursor) ?? null) {
        if (cursor === board.id) throw new Error("不能把画板移动到自己的子画板中。");
      }
    }

    const updatedAt = nowIso();
    const next = { ...board, parentId, updatedAt };
    await database.boards.put(next);
    await database.projects.update(board.projectId, { updatedAt });
    return next;
  });
}

/**
 * Deletes one board without cascading into its children. Children are promoted
 * to the deleted board's parent and every portal targeting the board is removed,
 * including connected edges, so the project never contains dangling shortcuts.
 */
export async function deleteBoardRecord(
  boardId: EntityId,
  database: KimnoteDatabase = kimnoteDb,
): Promise<DeleteBoardResult> {
  return database.transaction(
    "rw",
    database.projects,
    database.boards,
    database.nodes,
    database.edges,
    async () => {
      const board = await database.boards.get(boardId);
      if (!board) throw new Error(`Board not found: ${boardId}`);
      const project = await database.projects.get(board.projectId);
      if (!project) throw new Error(`Project not found: ${board.projectId}`);
      const projectBoards = await database.boards.where("projectId").equals(project.id).toArray();
      const remainingBoards = projectBoards
        .filter((candidate) => candidate.id !== boardId)
        .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id));
      if (!remainingBoards.length) throw new Error("项目至少需要保留一个画板。");

      const timestamp = nowIso();
      const nextBoardId = remainingBoards.some((candidate) => candidate.id === board.parentId)
        ? board.parentId!
        : remainingBoards[0].id;
      const promotedChildren = projectBoards
        .filter((candidate) => candidate.parentId === boardId)
        .map((candidate) => ({ ...candidate, parentId: board.parentId, updatedAt: timestamp }));
      const projectBoardIds = projectBoards.map((candidate) => candidate.id);
      const projectNodes = await database.nodes.where("boardId").anyOf(projectBoardIds).toArray();
      const boardNodeIds = projectNodes.filter((node) => node.boardId === boardId).map((node) => node.id);
      const portalNodeIds = projectNodes
        .filter((node) => node.boardId !== boardId && node.data.targetBoardId === boardId)
        .map((node) => node.id);
      const deletedNodeIds = new Set([...boardNodeIds, ...portalNodeIds]);
      const projectEdges = await database.edges.where("boardId").anyOf(projectBoardIds).toArray();
      const edgeIds = projectEdges
        .filter((edge) => edge.boardId === boardId || deletedNodeIds.has(edge.source) || deletedNodeIds.has(edge.target))
        .map((edge) => edge.id);

      if (edgeIds.length) await database.edges.bulkDelete(edgeIds);
      if (deletedNodeIds.size) await database.nodes.bulkDelete([...deletedNodeIds]);
      if (promotedChildren.length) await database.boards.bulkPut(promotedChildren);
      await database.boards.delete(boardId);
      await database.projects.update(project.id, {
        activeBoardId: project.activeBoardId === boardId ? nextBoardId : project.activeBoardId,
        updatedAt: timestamp,
      });
      return { deletedBoardId: boardId, nextBoardId, deletedPortalNodeIds: portalNodeIds };
    },
  );
}

/**
 * Clones a board and its complete descendant tree in one transaction. Internal
 * board portals are remapped to the clone; shortcuts to boards outside the tree
 * deliberately continue to reference their original targets.
 */
export async function duplicateBoardTree(
  sourceBoardId: EntityId,
  options: DuplicateBoardTreeOptions = {},
  database: KimnoteDatabase = kimnoteDb,
): Promise<DuplicateBoardTreeResult> {
  return database.transaction(
    "rw",
    database.projects,
    database.boards,
    database.nodes,
    database.edges,
    async () => {
      const source = await database.boards.get(sourceBoardId);
      if (!source) throw new Error(`Board not found: ${sourceBoardId}`);
      const project = await database.projects.get(source.projectId);
      if (!project) throw new Error(`Project not found: ${source.projectId}`);

      const projectBoards = await database.boards.where("projectId").equals(source.projectId).toArray();
      const childrenByParent = new Map<EntityId, BoardRecord[]>();
      for (const board of projectBoards) {
        if (!board.parentId) continue;
        const children = childrenByParent.get(board.parentId) ?? [];
        children.push(board);
        childrenByParent.set(board.parentId, children);
      }
      const sourceTree: BoardRecord[] = [];
      const queue = [source];
      const seen = new Set<EntityId>();
      while (queue.length) {
        const current = queue.shift()!;
        if (seen.has(current.id)) continue;
        seen.add(current.id);
        sourceTree.push(current);
        queue.push(...(childrenByParent.get(current.id) ?? []));
      }

      const requestedParentId = options.parentId === undefined ? source.parentId : options.parentId;
      if (requestedParentId) {
        const parent = projectBoards.find((board) => board.id === requestedParentId);
        if (!parent) throw new Error("父级画板必须属于同一个项目。");
        if (seen.has(parent.id)) throw new Error("不能把画板副本放进它所复制的子树中。");
      }

      const timestamp = nowIso();
      const boardIdMap = new Map(sourceTree.map((board) => [board.id, createEntityId("board")]));
      const sourceBoardIds = sourceTree.map((board) => board.id);
      const [sourceNodes, sourceEdges] = await Promise.all([
        database.nodes.where("boardId").anyOf(sourceBoardIds).toArray(),
        database.edges.where("boardId").anyOf(sourceBoardIds).toArray(),
      ]);
      const nodeIdMap = new Map(sourceNodes.map((node) => [node.id, createEntityId("node")]));
      const rootOrder = projectBoards.length;
      const clonedBoards = sourceTree.map((board) => ({
        ...board,
        id: boardIdMap.get(board.id)!,
        parentId: board.id === source.id
          ? requestedParentId ?? null
          : board.parentId ? boardIdMap.get(board.parentId) ?? requestedParentId ?? null : null,
        title: board.id === source.id ? options.title?.trim() || `${source.title} 副本` : board.title,
        order: board.id === source.id ? rootOrder : board.order,
        viewport: { ...board.viewport },
        createdAt: timestamp,
        updatedAt: timestamp,
      }));
      const clonedNodes: StoredNode[] = sourceNodes.map((node) => ({
        ...node,
        id: nodeIdMap.get(node.id)!,
        boardId: boardIdMap.get(node.boardId)!,
        parentId: node.parentId ? nodeIdMap.get(node.parentId) : undefined,
        position: { ...node.position },
        data: remapBoardReferences(node.data, boardIdMap, timestamp),
        createdAt: timestamp,
        updatedAt: timestamp,
      }));
      const clonedEdges: StoredEdge[] = sourceEdges.map((edge) => ({
        ...edge,
        id: createEntityId("edge"),
        boardId: boardIdMap.get(edge.boardId)!,
        source: nodeIdMap.get(edge.source)!,
        target: nodeIdMap.get(edge.target)!,
        data: edge.data ? { ...edge.data, createdAt: timestamp, updatedAt: timestamp } : edge.data,
        createdAt: timestamp,
        updatedAt: timestamp,
      }));

      await database.boards.bulkAdd(clonedBoards);
      if (clonedNodes.length) await database.nodes.bulkAdd(clonedNodes);
      if (clonedEdges.length) await database.edges.bulkAdd(clonedEdges);
      await database.projects.update(project.id, { updatedAt: timestamp });
      return { root: clonedBoards[0], boards: clonedBoards, boardIdMap };
    },
  );
}

function remapBoardReferences(
  data: StoredNode["data"],
  boardIdMap: ReadonlyMap<EntityId, EntityId>,
  timestamp: string,
): StoredNode["data"] {
  const next = { ...data, createdAt: timestamp, updatedAt: timestamp };
  if (data.targetBoardId && boardIdMap.has(data.targetBoardId)) {
    next.targetBoardId = boardIdMap.get(data.targetBoardId);
  }
  if (data.props) {
    const props = { ...data.props };
    for (const key of ["boardId", "targetBoardId"] as const) {
      const value = props[key];
      if (typeof value === "string" && boardIdMap.has(value)) props[key] = boardIdMap.get(value);
    }
    next.props = props;
  }
  return next;
}

/** Returns the most recently opened project, creating a usable project/board when empty. */
export async function initializeDefaultProject(
  database: KimnoteDatabase = kimnoteDb,
): Promise<CreatedProject> {
  return database.transaction("rw", database.projects, database.boards, async () => {
    const existingProject = await database.projects.orderBy("lastOpenedAt").last();
    if (!existingProject) {
      const created = makeDefaultProjectRecords({});
      await database.projects.add(created.project);
      await database.boards.add(created.board);
      return created;
    }

    const activeBoard = await database.boards.get(existingProject.activeBoardId);
    if (activeBoard?.projectId === existingProject.id) {
      return { project: existingProject, board: activeBoard };
    }

    const firstBoard = await database.boards
      .where("projectId")
      .equals(existingProject.id)
      .sortBy("order")
      .then((boards) => boards[0]);
    if (firstBoard) {
      await database.projects.update(existingProject.id, { activeBoardId: firstBoard.id });
      return {
        project: { ...existingProject, activeBoardId: firstBoard.id },
        board: firstBoard,
      };
    }

    const timestamp = nowIso();
    const board: BoardRecord = {
      id: createEntityId("board"),
      projectId: existingProject.id,
      parentId: null,
      title: "Main Board",
      order: 0,
      viewport: { ...DEFAULT_VIEWPORT },
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    const project = {
      ...existingProject,
      activeBoardId: board.id,
      updatedAt: timestamp,
      lastOpenedAt: timestamp,
    };
    await database.boards.add(board);
    await database.projects.put(project);
    return { project, board };
  });
}

export async function getBoardSnapshot(
  boardId: EntityId,
  database: KimnoteDatabase = kimnoteDb,
): Promise<BoardSnapshot | null> {
  return database.transaction(
    "r",
    database.projects,
    database.boards,
    database.nodes,
    database.edges,
    async () => {
      const board = await database.boards.get(boardId);
      if (!board) return null;

      const project = await database.projects.get(board.projectId);
      if (!project) return null;

      const [nodes, edges] = await Promise.all([
        database.nodes
          .where("[boardId+zIndex]")
          .between([boardId, Dexie.minKey], [boardId, Dexie.maxKey])
          .toArray(),
        database.edges.where("boardId").equals(boardId).toArray(),
      ]);
      return { project, board, nodes, edges };
    },
  );
}

export interface BoardChanges {
  upsertNodes?: readonly StoredNode[];
  deleteNodeIds?: readonly EntityId[];
  upsertEdges?: readonly StoredEdge[];
  deleteEdgeIds?: readonly EntityId[];
  viewport?: Viewport;
}

/**
 * Applies one interaction batch atomically. Node deletion cascades to connected edges.
 * Call this on gesture completion rather than on every pointer-move frame.
 */
export async function applyBoardChanges(
  boardId: EntityId,
  changes: BoardChanges,
  database: KimnoteDatabase = kimnoteDb,
): Promise<void> {
  const upsertNodes = [...(changes.upsertNodes ?? [])];
  const upsertEdges = [...(changes.upsertEdges ?? [])];
  const requestedNodeDeletes = unique(changes.deleteNodeIds ?? []);
  const requestedEdgeDeletes = unique(changes.deleteEdgeIds ?? []);
  const timestamp = nowIso();

  assertViewport(changes.viewport);
  assertRecordsBelongToBoard(boardId, upsertNodes, "node");
  assertRecordsBelongToBoard(boardId, upsertEdges, "edge");

  await database.transaction(
    "rw",
    database.projects,
    database.boards,
    database.nodes,
    database.edges,
    async () => {
      const board = await database.boards.get(boardId);
      if (!board) throw new Error(`Board not found: ${boardId}`);

      await assertNoCrossBoardIdCollision(upsertNodes, database.nodes, boardId, "node");
      await assertNoCrossBoardIdCollision(upsertEdges, database.edges, boardId, "edge");

      const existingNodesToDelete = await database.nodes.bulkGet(requestedNodeDeletes);
      const nodeIdsToDelete = existingNodesToDelete
        .filter((node): node is StoredNode => node?.boardId === boardId)
        .map((node) => node.id);
      const deletedNodeSet = new Set(nodeIdsToDelete);

      const existingEdgesToDelete = await database.edges.bulkGet(requestedEdgeDeletes);
      const edgeIdsToDelete = existingEdgesToDelete
        .filter((edge): edge is StoredEdge => edge?.boardId === boardId)
        .map((edge) => edge.id);

      if (deletedNodeSet.size > 0) {
        const connectedEdgeIds = await database.edges
          .where("boardId")
          .equals(boardId)
          .filter((edge) => deletedNodeSet.has(edge.source) || deletedNodeSet.has(edge.target))
          .primaryKeys();
        edgeIdsToDelete.push(...connectedEdgeIds);
      }

      const candidateEdges = upsertEdges.filter(
        (edge) => !deletedNodeSet.has(edge.source) && !deletedNodeSet.has(edge.target),
      );
      if (candidateEdges.length > 0) {
        const endpointIds = unique(
          candidateEdges.flatMap((edge) => [edge.source, edge.target]),
        );
        const existingEndpoints = await database.nodes.bulkGet(endpointIds);
        const availableNodeIds = new Set(
          existingEndpoints
            .filter((node): node is StoredNode => node?.boardId === boardId)
            .map((node) => node.id),
        );
        for (const node of upsertNodes) {
          if (!deletedNodeSet.has(node.id)) availableNodeIds.add(node.id);
        }
        for (const edge of candidateEdges) {
          if (!availableNodeIds.has(edge.source) || !availableNodeIds.has(edge.target)) {
            throw new Error(`Edge ${edge.id} references a missing node.`);
          }
        }
      }

      const edgeDeleteSet = new Set(edgeIdsToDelete);
      const nodesToPut = upsertNodes.filter((node) => !deletedNodeSet.has(node.id));
      const edgesToPut = candidateEdges.filter((edge) => !edgeDeleteSet.has(edge.id));

      if (edgeDeleteSet.size > 0) await database.edges.bulkDelete([...edgeDeleteSet]);
      if (deletedNodeSet.size > 0) await database.nodes.bulkDelete([...deletedNodeSet]);
      if (nodesToPut.length > 0) await database.nodes.bulkPut(nodesToPut);
      if (edgesToPut.length > 0) await database.edges.bulkPut(edgesToPut);

      await database.boards.update(boardId, {
        viewport: changes.viewport ? { ...changes.viewport } : board.viewport,
        updatedAt: timestamp,
      });
      await database.projects.update(board.projectId, { updatedAt: timestamp });
    },
  );
}

export async function deleteProject(
  projectId: EntityId,
  database: KimnoteDatabase = kimnoteDb,
): Promise<void> {
  await database.transaction(
    "rw",
    [
      database.projects,
      database.boards,
      database.nodes,
      database.edges,
      database.assets,
      database.assetRefs,
    ],
    async () => {
      const boardIds = await database.boards.where("projectId").equals(projectId).primaryKeys();
      const [nodeIds, edgeIds, assetRefs] = await Promise.all([
        boardIds.length
          ? database.nodes.where("boardId").anyOf(boardIds).primaryKeys()
          : Promise.resolve([]),
        boardIds.length
          ? database.edges.where("boardId").anyOf(boardIds).primaryKeys()
          : Promise.resolve([]),
        database.assetRefs.where("projectId").equals(projectId).toArray(),
      ]);

      if (edgeIds.length) await database.edges.bulkDelete(edgeIds);
      if (nodeIds.length) await database.nodes.bulkDelete(nodeIds);
      if (boardIds.length) await database.boards.bulkDelete(boardIds);
      await database.assetRefs.where("projectId").equals(projectId).delete();
      await database.projects.delete(projectId);

      for (const { hash } of assetRefs) {
        if ((await database.assetRefs.where("hash").equals(hash).count()) === 0) {
          await database.assets.delete(hash);
        }
      }
    },
  );
}

export interface AssetMetadata {
  originalName?: string;
  mimeType?: string;
  createdAt?: string;
}

export async function sha256Blob(blob: Blob): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error("Web Crypto SHA-256 is unavailable in this environment.");
  }
  const digest = await globalThis.crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("");
}

/** Stores one copy per digest and adds an idempotent project reference. */
export async function putAssetBlob(
  projectId: EntityId,
  blob: Blob,
  metadata: AssetMetadata = {},
  database: KimnoteDatabase = kimnoteDb,
): Promise<AssetRecord> {
  const hash = await sha256Blob(blob);
  const candidate: AssetRecord = {
    hash,
    blob,
    mimeType: metadata.mimeType || blob.type || "application/octet-stream",
    byteLength: blob.size,
    originalName: metadata.originalName,
    createdAt: metadata.createdAt ?? nowIso(),
  };

  return database.transaction(
    "rw",
    database.projects,
    database.assets,
    database.assetRefs,
    async () => {
      if (!(await database.projects.get(projectId))) {
        throw new Error(`Project not found: ${projectId}`);
      }
      const existing = await database.assets.get(hash);
      if (!existing) await database.assets.add(candidate);
      await database.assetRefs.put({ projectId, hash });
      return existing ?? candidate;
    },
  );
}

export async function linkAssetToProject(
  projectId: EntityId,
  hash: string,
  database: KimnoteDatabase = kimnoteDb,
): Promise<void> {
  await database.transaction(
    "rw",
    database.projects,
    database.assets,
    database.assetRefs,
    async () => {
      if (!(await database.projects.get(projectId))) throw new Error(`Project not found: ${projectId}`);
      if (!(await database.assets.get(hash))) throw new Error(`Asset not found: ${hash}`);
      await database.assetRefs.put({ projectId, hash });
    },
  );
}

export async function releaseAssetReference(
  projectId: EntityId,
  hash: string,
  database: KimnoteDatabase = kimnoteDb,
): Promise<void> {
  await database.transaction("rw", database.assets, database.assetRefs, async () => {
    await database.assetRefs.delete([projectId, hash]);
    if ((await database.assetRefs.where("hash").equals(hash).count()) === 0) {
      await database.assets.delete(hash);
    }
  });
}

export async function getAssetBlob(
  hash: string,
  database: KimnoteDatabase = kimnoteDb,
): Promise<Blob | null> {
  return (await database.assets.get(hash))?.blob ?? null;
}

export async function getProjectAssets(
  projectId: EntityId,
  database: KimnoteDatabase = kimnoteDb,
): Promise<AssetRecord[]> {
  const refs = await database.assetRefs.where("projectId").equals(projectId).toArray();
  const assets = await database.assets.bulkGet(refs.map(({ hash }) => hash));
  return assets.filter((asset): asset is AssetRecord => asset !== undefined);
}

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

function makeDefaultProjectRecords(options: CreateProjectOptions): CreatedProject {
  const timestamp = options.now ?? nowIso();
  const projectId = options.id ?? createEntityId("project");
  const boardId = options.boardId ?? createEntityId("board");
  const project: ProjectRecord = {
    id: projectId,
    title: options.title?.trim() || "Untitled Project",
    schemaVersion: KIMNOTE_SCHEMA_VERSION,
    activeBoardId: boardId,
    createdAt: timestamp,
    updatedAt: timestamp,
    lastOpenedAt: timestamp,
  };
  const board: BoardRecord = {
    id: boardId,
    projectId,
    parentId: null,
    title: options.boardTitle?.trim() || "Main Board",
    order: 0,
    viewport: { ...DEFAULT_VIEWPORT },
    createdAt: timestamp,
    updatedAt: timestamp,
  };
  return { project, board };
}

function assertViewport(viewport: Viewport | undefined): void {
  if (!viewport) return;
  if (![viewport.x, viewport.y, viewport.zoom].every(Number.isFinite) || viewport.zoom <= 0) {
    throw new Error("Viewport must contain finite x/y coordinates and a positive zoom.");
  }
}

function assertRecordsBelongToBoard(
  boardId: EntityId,
  records: ReadonlyArray<{ id: EntityId; boardId: EntityId }>,
  kind: "node" | "edge",
): void {
  for (const record of records) {
    if (record.boardId !== boardId) {
      throw new Error(`${kind} ${record.id} belongs to another board.`);
    }
  }
}

async function assertNoCrossBoardIdCollision<T extends { id: EntityId; boardId: EntityId }>(
  records: readonly T[],
  table: Table<T, EntityId>,
  boardId: EntityId,
  kind: "node" | "edge",
): Promise<void> {
  if (records.length === 0) return;
  const existing = await table.bulkGet(unique(records.map(({ id }) => id)));
  for (const record of existing) {
    if (record && record.boardId !== boardId) {
      throw new Error(`${kind} ${record.id} already belongs to another board.`);
    }
  }
}
